import {
  getRuntimeConfig,
  isRuntimeConfigLoaded,
} from "@/config/runtimeConfig";
import axios from "axios";
import { getAdminAccessToken } from "@lib/storeManager";

function resolveApiBaseUrl(): string {
  if (isRuntimeConfigLoaded()) {
    return getRuntimeConfig().apiUrl;
  }
  return (
    import.meta.env.VITE_JAIN_ELIBRARY_API_URL ?? "http://localhost:8000/api"
  );
}

export const adminAPI = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

adminAPI.interceptors.request.use(config => {
  const token = getAdminAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Verbose logging only in dev — avoid extra main-thread work in production
  if (
    import.meta.env.DEV &&
    config.url?.includes("/books/") &&
    !config.url?.includes("/books/featured/")
  ) {
    console.log("API Request URL:", `${config.baseURL}${config.url}`);
    console.log("API Request Params:", config.params);
    console.log(
      "API Request Full URL with params:",
      `${config.baseURL}${config.url}?${new URLSearchParams(config.params as any).toString()}`
    );
  }
  return config;
});

// Response interceptor for error handling
adminAPI.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      localStorage.removeItem("adminAccessToken");
      localStorage.removeItem("adminRefreshToken");
      localStorage.removeItem("adminUserInfo");

      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

export interface Statistics {
  /** Total rows on site — matches backend `Count("sr_no")` aggregation */
  files_on_website?: number;
  // Core counts
  total_books: number;
  total_users: number;
  total_downloads: number;
  total_views: number;
  total_donations: number;
  total_donation_amount: number;

  // Content counts
  total_catalogues: number;
  total_magazines: number;
  total_authors: number;
  total_publishers: number;
  total_articles: number;
  total_audio: number;
  total_manuscripts: number;
  total_agam: number;
  total_collections: number;

  // Request counts
  total_upload_requests: number;
  total_book_requests: number;
  total_feedback: number;

  // Recent activity (last 7 days)
  recent_downloads: number;
  recent_uploads: number;
  recent_book_requests: number;
  recent_donations: number;
  recent_feedback: number;
}

export interface Book {
  sr_no: string;
  title_english: string;
  title_language?: string;
  author?: string;
  editor?: string;
  translator?: string;
  publisher?: string;
  pub_year?: string;
  pages?: number;
  language?: string;
  classification?: string;
  /** When true, book appears in the public home “Featured” section (via GET /books/featured/). */
  is_featured?: boolean;
  /** DB column `featured` (boolean); same meaning as is_featured in API responses. */
  featured?: boolean;
  featured_order?: number | null;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface MdbTableStat {
  table: string;
  /** Current live row count; null if the table is missing. */
  rows: number | null;
  /** Snapshot (_ss) row count; null when no snapshot exists for it. */
  snapshot_rows: number | null;
  /** ISO timestamp of the last successful CSV load into this table. */
  last_load_at: string | null;
}

export interface MergedTableStat {
  table: string;
  /** Current live row count; null if the table is missing. */
  rows: number | null;
  /** Saved-snapshot (_ss) row count; null when no snapshot exists. */
  snapshot_rows: number | null;
}

export interface UploadRequest {
  id: number;
  email: string;
  book_name: string;
  author: string;
  publisher?: string;
  year?: string;
  pages?: number;
  language: string;
  status: string;
  created_on: string;
}

export interface BookRequest {
  id: number;
  email: string;
  book_name: string;
  author?: string;
  publisher?: string;
  year?: string;
  language?: string;
  purpose?: string;
  status: string;
  created_on: string;
}

export interface Donation {
  id: number;
  name: string;
  email: string;
  phone?: string;
  amount?: number;
  currency: string;
  payment_method?: string;
  message?: string;
  is_anonymous: boolean;
  created_on: string;
}

export interface Feedback {
  id: number;
  name: string;
  email: string;
  subject?: string;
  location?: string;
  message: string;
  is_read: boolean;
  created_on: string;
}

/** CMS page (About, Committee, Contact) — `GET/PATCH /site-content-pages/{slug}/` */
export interface SiteContentPage {
  id: number;
  slug: string;
  title: string;
  body_html: string;
  is_active: boolean;
  created_on?: string;
  updated_on?: string;
}

/** Order-confirmation email, one per storefront source. */
export type OrderEmailSource =
  | "india-purchase"
  | "usa-purchase"
  | "reuse-recycle";

/** Order confirmation email template — `GET/PATCH /order-email-templates/{source}/` */
export interface OrderEmailTemplate {
  id: number;
  source: OrderEmailSource;
  subject: string;
  body_html: string;
  is_active: boolean;
  created_on?: string;
  updated_on?: string;
}

/** Home page banner strip — `GET/POST/PATCH /home-banners/` */
export interface HomeBannerRow {
  id: number;
  sort_order: number;
  image_url: string;
  description: string;
  link_url: string;
  is_active: boolean;
  created_on?: string;
  updated_on?: string;
}

/** Committee / Contact — person cards under a section */
export interface SitePageCardRow {
  id: number;
  section: number;
  sort_order: number;
  name: string;
  email: string;
  phone: string;
  role: string;
  detail_line: string;
  is_active: boolean;
  created_on?: string;
  updated_on?: string;
}

export interface SitePageCardSectionRow {
  id: number;
  page_slug: string;
  title: string;
  sort_order: number;
  grid_columns: number;
  cards: SitePageCardRow[];
  is_active: boolean;
  created_on?: string;
  updated_on?: string;
}

export interface ShoppingProduct {
  id: number;
  item_id: string;
  sr_no?: string;
  age: string;
  book_title: string;
  description: string;
  price: number;
  base_cost?: number | null;
  additional_cost?: number | null;
  count: number | string;
  country: string;
}

export interface UserSearchQueryRow {
  id: number;
  user_id: number;
  user_email: string;
  user_username: string;
  query: string;
  search_type: string;
  ip_address?: string;
  city?: string;
  state?: string;
  country?: string;
  browser_name?: string | null;
  browser_version?: string | null;
  os_name?: string | null;
  os_version?: string | null;
  device_type?: string | null;
  user_agent?: string | null;
  created_at: string;
}

/** Admin CRUD for `site_visit` rows — `/site-visit-records/`. */
export interface SiteVisitRecord {
  id: number;
  created_at: string;
  path: string;
  referrer: string;
  user_agent_hash: string;
  ip_address?: string;
  city?: string;
  state?: string;
  country?: string;
  browser_name?: string | null;
  browser_version?: string | null;
  os_name?: string | null;
  os_version?: string | null;
  device_type?: string | null;
  user_agent?: string | null;
}
export interface UserDownload {
  id: number;
  uid: number | null;
  username: string;
  book_sr_no: string;
  sr_no: number;
  filename: string;
  download_date: string;
  ddate: string;
  count: number;
  ipaddr: string;
  ip_address?: string;
  city?: string;
  state?: string;
  country: string;
  book_title?: string;
  browser_name?: string | null;
  browser_version?: string | null;
  os_name?: string | null;
  os_version?: string | null;
  device_type?: string | null;
  user_agent?: string | null;
}

export interface ShoppingOrder {
  id: number;
  order_number: string;
  user: any;
  items: any[];
  shipping_address: any;
  payment_method: string;
  paypal_order_id?: string;
  paypal_transaction_id?: string;
  payment_proof_url?: string;
  payment_proof_notes?: string;
  subtotal: string | number;
  shipping_cost: string | number;
  donation_amount: string | number;
  total: string | number;
  source: string;
  status: string;
  created_at: string;
  updated_at: string;
  item_count: number;
  shipping_full_name: string;
  shipping_email: string;
  courier_name?: string | null;
  tracking_number?: string | null;
  date_shipped?: string | null;
  payment_received?: boolean;
  date_received?: string | null;
}

export interface ShoppingOrderingAddress {
  id: number;
  country_code: string | null;
  favor_of: string | null;
  check_mailing_address: string | null;
  email_ids: string | null;
}

export interface ShippingCost {
  id: number;
  country: string;
  currency: string;
  base_cost: string | number;
  additional_cost: string | number;
}

export type DonationButtonAction =
  | "none"
  | "paypal"
  | "donate_form"
  | "fill_form"
  | "url";

export interface DonationOption {
  id: number;
  title: string;
  details_html: string;
  button_action: DonationButtonAction;
  button_label: string;
  button_url: string;
  icon_style: "brown" | "dark";
  sort_order: number;
  is_active: boolean;
  created_on?: string;
  updated_on?: string;
}

export interface RegisteredUser {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  phone?: string;
  phone_number?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  bio?: string;
  is_active?: boolean;
  date_joined?: string;
  user_type?: string;
  download_limit?: number;
  downcount?: number;
  bio?: string;
}

// Module-level cache: deduplicates concurrent calls and caches for 5 minutes
let _statsCache: { data: Statistics; ts: number } | null = null;
let _statsInflight: Promise<Statistics> | null = null;
const STATS_TTL = 5 * 60 * 1000;

class AdminService {
  // Statistics
  getStatistics = async (): Promise<Statistics> => {
    if (_statsCache && Date.now() - _statsCache.ts < STATS_TTL) {
      return _statsCache.data;
    }
    if (_statsInflight) {
      return _statsInflight;
    }
    _statsInflight = adminAPI
      .get<Statistics>("/statistics/")
      .then(r => {
        _statsCache = { data: r.data, ts: Date.now() };
        _statsInflight = null;
        return r.data;
      })
      .catch(e => {
        _statsInflight = null;
        throw e;
      });
    return _statsInflight;
  };

  // Books
  getBooks = async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    author?: string;
    category?: string;
    language?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<Book>> => {
    // Log the actual request being made
    console.log("getBooks API call - params:", params);

    // Ensure search parameter is properly included if provided
    const requestParams: Record<string, any> = { ...params };
    if (params?.search && params.search.trim().length > 0) {
      requestParams.search = params.search.trim();
    }

    const response = await adminAPI.get<PaginatedResponse<Book>>("/books/", {
      params: requestParams,
    });

    // Log the actual URL that was called
    console.log("getBooks API call - full URL:", response.config.url);
    console.log("getBooks API call - request params:", response.config.params);
    console.log("getBooks API call - response count:", response.data.count);
    console.log(
      "getBooks API call - response results length:",
      response.data.results?.length
    );

    return response.data;
  };

  getBook = async (srNo: string): Promise<Book> => {
    const response = await adminAPI.get<Book>(`/books/${srNo}/`);
    return response.data;
  };

  /** Same list the public home page uses (`GET /books/featured/`). */
  getFeaturedBooks = async (): Promise<Book[]> => {
    const response = await adminAPI.get<
      Book[] | PaginatedResponse<Book> | { results?: Book[] }
    >("/books/featured/");
    const d = response.data;
    if (Array.isArray(d)) {
      return d;
    }
    if (
      d &&
      typeof d === "object" &&
      "results" in d &&
      Array.isArray(d.results)
    ) {
      return d.results;
    }
    return [];
  };

  /**
   * Toggle homepage featured flag (`PATCH /books/{sr_no}/feature/`).
   * Updates `ElibraryMeta.featured`; list cache is invalidated server-side so GET /books/featured/ stays fresh.
   */
  patchBook = async (
    srNo: string,
    data: { is_featured: boolean }
  ): Promise<Book> => {
    const encoded = encodeURIComponent(srNo);
    const response = await adminAPI.patch<Book>(
      `/books/${encoded}/feature/`,
      data
    );
    return response.data;
  };

  // Upload Requests
  getUploadRequests = async (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    search?: string;
  }): Promise<PaginatedResponse<UploadRequest>> => {
    const response = await adminAPI.get<PaginatedResponse<UploadRequest>>(
      "/upload-requests/",
      { params }
    );
    return response.data;
  };

  updateUploadRequest = async (
    id: number,
    data: Partial<UploadRequest>
  ): Promise<UploadRequest> => {
    const response = await adminAPI.patch<UploadRequest>(
      `/upload-requests/${id}/`,
      data
    );
    return response.data;
  };

  // Book Requests
  getBookRequests = async (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    search?: string;
  }): Promise<PaginatedResponse<BookRequest>> => {
    const response = await adminAPI.get<PaginatedResponse<BookRequest>>(
      "/book-requests/",
      { params }
    );
    return response.data;
  };

  updateBookRequest = async (
    id: number,
    data: Partial<BookRequest>
  ): Promise<BookRequest> => {
    const response = await adminAPI.patch<BookRequest>(
      `/book-requests/${id}/`,
      data
    );
    return response.data;
  };

  // Donations
  getDonations = async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
  }): Promise<PaginatedResponse<Donation>> => {
    const response = await adminAPI.get<PaginatedResponse<Donation>>(
      "/donations/",
      { params }
    );
    return response.data;
  };

  // Feedback
  getFeedback = async (params?: {
    page?: number;
    page_size?: number;
    is_read?: boolean;
    search?: string;
  }): Promise<PaginatedResponse<Feedback>> => {
    const response = await adminAPI.get<PaginatedResponse<Feedback>>(
      "/feedback/",
      { params }
    );
    return response.data;
  };

  updateFeedback = async (
    id: number,
    data: Partial<Feedback>
  ): Promise<Feedback> => {
    const response = await adminAPI.patch<Feedback>(`/feedback/${id}/`, data);
    return response.data;
  };

  // Articles
  getArticles = async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    category?: string;
    language?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<any>> => {
    const response = await adminAPI.get<PaginatedResponse<any>>("/articles/", {
      params,
    });
    return response.data;
  };

  // Audio
  getAudio = async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    category?: string;
    language?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<any>> => {
    const response = await adminAPI.get<PaginatedResponse<any>>("/audio/", {
      params,
    });
    return response.data;
  };

  // Manuscripts
  getManuscripts = async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    category?: string;
    language?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<any>> => {
    const response = await adminAPI.get<PaginatedResponse<any>>(
      "/manuscripts/",
      { params }
    );
    return response.data;
  };

  // Magazines
  getMagazines = async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    language?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<any>> => {
    const response = await adminAPI.get<PaginatedResponse<any>>("/magazines/", {
      params,
    });
    return response.data;
  };

  // Agam
  getAgam = async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    language?: string; // Searches both mool_language and trans_language (case-insensitive)
    ordering?: string;
  }): Promise<PaginatedResponse<any>> => {
    const response = await adminAPI.get<PaginatedResponse<any>>("/agam/", {
      params,
    });
    return response.data;
  };

  // Authors
  getAuthors = async (params?: {
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<any>> => {
    const response = await adminAPI.get<PaginatedResponse<any>>("/authors/", {
      params,
    });
    return response.data;
  };

  // Publishers
  getPublishers = async (params?: {
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<any>> => {
    const response = await adminAPI.get<PaginatedResponse<any>>(
      "/publishers/",
      {
        params,
      }
    );
    return response.data;
  };

  // Languages
  getLanguages = async (): Promise<any[]> => {
    const response = await adminAPI.get<any>("/languages/");
    return response.data.results ?? response.data;
  };

  // Shopping Products
  getShoppingProducts = async (params?: {
    country?: string;
    ordering?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<ShoppingProduct>> => {
    const response = await adminAPI.get<PaginatedResponse<ShoppingProduct>>(
      "/shopping-products/",
      { params }
    );
    return response.data;
  };

  createShoppingProduct = async (
    data: Partial<ShoppingProduct>
  ): Promise<ShoppingProduct> => {
    const response = await adminAPI.post<ShoppingProduct>(
      "/shopping-products/",
      data
    );
    return response.data;
  };

  updateShoppingProduct = async (
    id: number,
    data: Partial<ShoppingProduct>
  ): Promise<ShoppingProduct> => {
    const response = await adminAPI.patch<ShoppingProduct>(
      `/shopping-products/${id}/`,
      data
    );
    return response.data;
  };

  deleteShoppingProduct = async (id: number): Promise<void> => {
    await adminAPI.delete(`/shopping-products/${id}/`);
  };

  // User search history (admin list)
  getUserSearchQueries = async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    ordering?: string;
    search_type?: string;
  }): Promise<PaginatedResponse<UserSearchQueryRow>> => {
    const response = await adminAPI.get<PaginatedResponse<UserSearchQueryRow>>(
      "/user-search-queries/",
      { params }
    );
    return response.data;
  };

  // Site visits (`site_visit` table) — admin full CRUD
  getSiteVisitRecords = async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<SiteVisitRecord>> => {
    const response = await adminAPI.get<PaginatedResponse<SiteVisitRecord>>(
      "/site-visit-records/",
      { params }
    );
    return response.data;
  };

  createSiteVisitRecord = async (
    data: Partial<Omit<SiteVisitRecord, "id" | "created_at">>
  ): Promise<SiteVisitRecord> => {
    const response = await adminAPI.post<SiteVisitRecord>(
      "/site-visit-records/",
      data
    );
    return response.data;
  };

  updateSiteVisitRecord = async (
    id: number,
    data: Partial<Omit<SiteVisitRecord, "id" | "created_at">>
  ): Promise<SiteVisitRecord> => {
    const response = await adminAPI.patch<SiteVisitRecord>(
      `/site-visit-records/${id}/`,
      data
    );
    return response.data;
  };

  deleteSiteVisitRecord = async (id: number): Promise<void> => {
    await adminAPI.delete(`/site-visit-records/${id}/`);
  };

  // User Downloads
  getUserDownloads = async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<UserDownload>> => {
    const response = await adminAPI.get<PaginatedResponse<UserDownload>>(
      "/user-downloads/",
      { params }
    );
    return response.data;
  };

  deleteUserDownload = async (id: number): Promise<void> => {
    await adminAPI.delete(`/user-downloads/${id}/`);
  };

  /**
   * Recalculate and persist per-user unique download counts (downcount).
   * Backend should: count distinct files per user from user-downloads, then update user/profile downcount.
   */
  recalculateUserDownloadCounts = async (): Promise<{
    success: boolean;
    message: string;
    users_updated?: number;
  }> => {
    const response = await adminAPI.get<{
      success: boolean;
      message: string;
      users_updated?: number;
    }>("/user-downloads/recalculate-counts/");
    return response.data;
  };

  // Auth
  changePassword = async (data: any): Promise<any> => {
    const response = await adminAPI.post("/auth/change-password/", data);
    return response.data;
  };

  getProfile = async (): Promise<any> => {
    const response = await adminAPI.get("/users/profile/");
    return response.data;
  };

  updateProfile = async (data: any): Promise<any> => {
    const response = await adminAPI.put("/users/profile/", data);
    return response.data;
  };

  // Registered Users (listing from users_userprofile)
  async getRegisteredUsers(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<RegisteredUser>> {
    const response = await adminAPI.get<any>("/profiles/", {
      params,
    });

    // Handle both paginated and non-paginated responses
    if (response.data?.results) {
      return response.data;
    } else if (Array.isArray(response.data)) {
      return {
        count: response.data.length,
        next: null,
        previous: null,
        results: response.data,
      };
    }

    return {
      count: 0,
      next: null,
      previous: null,
      results: [],
    };
  }

  async updateRegisteredUser(
    id: number,
    data: Partial<RegisteredUser>
  ): Promise<RegisteredUser> {
    const response = await adminAPI.patch<RegisteredUser>(
      `/profiles/${id}/`,
      data
    );
    return response.data;
  }

  async deleteRegisteredUser(id: number): Promise<void> {
    await adminAPI.delete(`/profiles/${id}/`);
  }

  // Admin management
  async getAdmins(params?: {
    page?: number;
    page_size?: number;
    search?: string;
  }): Promise<PaginatedResponse<RegisteredUser>> {
    const response = await adminAPI.get<any>("/profiles/", {
      params: { ...params, is_admin: true },
    });
    if (response.data?.results !== undefined) {
      return {
        results: response.data.results,
        count: response.data.count ?? 0,
      };
    }
    if (Array.isArray(response.data)) {
      return { results: response.data, count: response.data.length };
    }
    return { results: [], count: 0 };
  }

  async createAdmin(data: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
  }): Promise<any> {
    const response = await adminAPI.post("/auth/admin/signup/", data);
    return response.data;
  }

  async updateAdmin(
    id: number,
    data: Partial<RegisteredUser>
  ): Promise<RegisteredUser> {
    const response = await adminAPI.patch<RegisteredUser>(
      `/profiles/${id}/`,
      data
    );
    return response.data;
  }

  async deleteAdmin(id: number): Promise<void> {
    await adminAPI.delete(`/profiles/${id}/`);
  }

  // Shopping Orders
  getShoppingOrders = async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<ShoppingOrder>> => {
    const response = await adminAPI.get<PaginatedResponse<ShoppingOrder>>(
      "/shopping-orders/",
      { params }
    );
    return response.data;
  };

  deleteShoppingOrder = async (id: number): Promise<void> => {
    await adminAPI.delete(`/shopping-orders/${id}/`);
  };

  updateShoppingOrder = async (
    id: number,
    data: Partial<ShoppingOrder>
  ): Promise<ShoppingOrder> => {
    const response = await adminAPI.patch<ShoppingOrder>(
      `/shopping-orders/${id}/`,
      data
    );
    return response.data;
  };

  // Confirm that payment was received for an offline / India order.
  // Marks the order paid, moves pending → processing, and triggers the
  // treasurer "Payment Received" email on the backend.
  markShoppingOrderPaymentReceived = async (
    id: number
  ): Promise<{
    success: boolean;
    message: string;
    order_id: number;
    order_number: string;
    status: string;
  }> => {
    const response = await adminAPI.post(
      `/shopping-orders/${id}/mark-payment-received/`
    );
    return response.data;
  };

  // Shopping ordering addresses (shopping_ordering_info_master)
  getShoppingOrderingAddresses = async (params?: {
    page?: number;
    page_size?: number;
    country_code?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<ShoppingOrderingAddress>> => {
    const response = await adminAPI.get<
      PaginatedResponse<ShoppingOrderingAddress>
    >("/shopping-ordering-addresses/", { params });
    return response.data;
  };

  createShoppingOrderingAddress = async (
    data: Partial<ShoppingOrderingAddress>
  ): Promise<ShoppingOrderingAddress> => {
    const response = await adminAPI.post<ShoppingOrderingAddress>(
      "/shopping-ordering-addresses/",
      data
    );
    return response.data;
  };

  updateShoppingOrderingAddress = async (
    id: number,
    data: Partial<ShoppingOrderingAddress>
  ): Promise<ShoppingOrderingAddress> => {
    const response = await adminAPI.patch<ShoppingOrderingAddress>(
      `/shopping-ordering-addresses/${id}/`,
      data
    );
    return response.data;
  };

  deleteShoppingOrderingAddress = async (id: number): Promise<void> => {
    await adminAPI.delete(`/shopping-ordering-addresses/${id}/`);
  };

  // Shipping costs (shipping_cost table)
  getShippingCosts = async (params?: {
    page?: number;
    page_size?: number;
    country?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<ShippingCost>> => {
    const response = await adminAPI.get<PaginatedResponse<ShippingCost>>(
      "/shipping-costs/",
      { params }
    );
    return response.data;
  };

  createShippingCost = async (
    data: Partial<ShippingCost>
  ): Promise<ShippingCost> => {
    const response = await adminAPI.post<ShippingCost>(
      "/shipping-costs/",
      data
    );
    return response.data;
  };

  updateShippingCost = async (
    id: number,
    data: Partial<ShippingCost>
  ): Promise<ShippingCost> => {
    const response = await adminAPI.patch<ShippingCost>(
      `/shipping-costs/${id}/`,
      data
    );
    return response.data;
  };

  deleteShippingCost = async (id: number): Promise<void> => {
    await adminAPI.delete(`/shipping-costs/${id}/`);
  };

  // Donation options (Donate page cards)
  getDonationOptions = async (): Promise<DonationOption[]> => {
    const response = await adminAPI.get<
      DonationOption[] | PaginatedResponse<DonationOption>
    >("/donation-options/");
    const d = response.data;
    return Array.isArray(d) ? d : (d.results ?? []);
  };

  createDonationOption = async (
    data: Partial<DonationOption>
  ): Promise<DonationOption> => {
    const response = await adminAPI.post<DonationOption>(
      "/donation-options/",
      data
    );
    return response.data;
  };

  updateDonationOption = async (
    id: number,
    data: Partial<DonationOption>
  ): Promise<DonationOption> => {
    const response = await adminAPI.patch<DonationOption>(
      `/donation-options/${id}/`,
      data
    );
    return response.data;
  };

  deleteDonationOption = async (id: number): Promise<void> => {
    await adminAPI.delete(`/donation-options/${id}/`);
  };

  getLastMerge = async (): Promise<{
    last_merged_at: string | null;
    message: string;
    last_snapshot_at?: string | null;
    last_restored_at?: string | null;
    snapshot_exists?: boolean;
    table_stats?: MergedTableStat[];
  }> => {
    const response = await adminAPI.get<{
      last_merged_at: string | null;
      message: string;
      last_snapshot_at?: string | null;
      last_restored_at?: string | null;
      snapshot_exists?: boolean;
      table_stats?: MergedTableStat[];
    }>("/data-merge/");
    return response.data;
  };

  performDataMerge = async (): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await adminAPI.post<{ success: boolean; message: string }>(
      "/data-merge/"
    );
    return response.data;
  };

  saveMergedSnapshot = async (
    replace = false
  ): Promise<{
    success: boolean;
    message: string;
    last_snapshot_at?: string | null;
  }> => {
    const response = await adminAPI.post<{
      success: boolean;
      message: string;
      last_snapshot_at?: string | null;
    }>("/data-merge/snapshot/save/", { replace });
    return response.data;
  };

  restoreMergedSnapshot = async (): Promise<{
    success: boolean;
    message: string;
    last_restored_at?: string | null;
  }> => {
    const response = await adminAPI.post<{
      success: boolean;
      message: string;
      last_restored_at?: string | null;
    }>("/data-merge/snapshot/restore/");
    return response.data;
  };

  getRegistrationSettings = async (): Promise<{
    enabled: boolean;
    disabled_message: string;
    updated_at: string | null;
    updated_by: { id: number; email: string; name: string } | null;
  }> => {
    const response = await adminAPI.get<{
      enabled: boolean;
      disabled_message: string;
      updated_at: string | null;
      updated_by: { id: number; email: string; name: string } | null;
    }>("/registration-settings/");
    return response.data;
  };

  updateRegistrationSettings = async (payload: {
    enabled?: boolean;
    disabled_message?: string;
  }): Promise<{
    enabled: boolean;
    disabled_message: string;
    updated_at: string | null;
    updated_by: { id: number; email: string; name: string } | null;
  }> => {
    const response = await adminAPI.patch<{
      enabled: boolean;
      disabled_message: string;
      updated_at: string | null;
      updated_by: { id: number; email: string; name: string } | null;
    }>("/registration-settings/", payload);
    return response.data;
  };

  getMdbImportStatus = async (): Promise<{
    tables: string[];
    table_stats?: MdbTableStat[];
    snapshot_exists: boolean;
    last_snapshot_at: string | null;
    last_restore_at: string | null;
    last_load_at: string | null;
    last_load_table: string | null;
  }> => {
    const response = await adminAPI.get<{
      tables: string[];
      table_stats?: MdbTableStat[];
      snapshot_exists: boolean;
      last_snapshot_at: string | null;
      last_restore_at: string | null;
      last_load_at: string | null;
      last_load_table: string | null;
    }>("/mdb-import/");
    return response.data;
  };

  saveMdbSnapshot = async (
    replace = false
  ): Promise<{
    success: boolean;
    message: string;
    last_snapshot_at?: string | null;
  }> => {
    const response = await adminAPI.post<{
      success: boolean;
      message: string;
      last_snapshot_at?: string | null;
    }>("/mdb-import/snapshot/save/", { replace });
    return response.data;
  };

  restoreMdbSnapshot = async (): Promise<{
    success: boolean;
    message: string;
    last_restore_at?: string | null;
  }> => {
    const response = await adminAPI.post<{
      success: boolean;
      message: string;
      last_restore_at?: string | null;
    }>("/mdb-import/snapshot/restore/");
    return response.data;
  };

  loadMdbCsv = async (
    tableName: string,
    file: File
  ): Promise<{
    success: boolean;
    message: string;
    table_name: string;
    row_count: number;
  }> => {
    const formData = new FormData();
    formData.append("table_name", tableName);
    formData.append("file", file);
    const response = await adminAPI.post<{
      success: boolean;
      message: string;
      table_name: string;
      row_count: number;
    }>("/mdb-import/load/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  };

  // Site content pages (public site About / Committee / Contact)
  getSiteContentPages = async (): Promise<SiteContentPage[]> => {
    const response = await adminAPI.get<
      SiteContentPage[] | PaginatedResponse<SiteContentPage>
    >("/site-content-pages/");
    const d = response.data;
    if (Array.isArray(d)) {
      return d;
    }
    return d.results ?? [];
  };

  getSiteContentPage = async (slug: string): Promise<SiteContentPage> => {
    const response = await adminAPI.get<SiteContentPage>(
      `/site-content-pages/${encodeURIComponent(slug)}/`
    );
    return response.data;
  };

  createSiteContentPage = async (data: {
    slug: string;
    title: string;
    body_html?: string;
    is_active?: boolean;
  }): Promise<SiteContentPage> => {
    const response = await adminAPI.post<SiteContentPage>(
      "/site-content-pages/",
      data
    );
    return response.data;
  };

  patchSiteContentPage = async (
    slug: string,
    data: Partial<Pick<SiteContentPage, "title" | "body_html" | "is_active">>
  ): Promise<SiteContentPage> => {
    const response = await adminAPI.patch<SiteContentPage>(
      `/site-content-pages/${encodeURIComponent(slug)}/`,
      data
    );
    return response.data;
  };

  // Order confirmation email templates (per storefront source)
  getOrderEmailTemplates = async (): Promise<OrderEmailTemplate[]> => {
    const response = await adminAPI.get<
      OrderEmailTemplate[] | PaginatedResponse<OrderEmailTemplate>
    >("/order-email-templates/");
    const d = response.data;
    return Array.isArray(d) ? d : (d.results ?? []);
  };

  getOrderEmailTemplate = async (
    source: string
  ): Promise<OrderEmailTemplate> => {
    const response = await adminAPI.get<OrderEmailTemplate>(
      `/order-email-templates/${encodeURIComponent(source)}/`
    );
    return response.data;
  };

  updateOrderEmailTemplate = async (
    source: string,
    data: Partial<
      Pick<OrderEmailTemplate, "subject" | "body_html" | "is_active">
    >
  ): Promise<OrderEmailTemplate> => {
    const response = await adminAPI.patch<OrderEmailTemplate>(
      `/order-email-templates/${encodeURIComponent(source)}/`,
      data
    );
    return response.data;
  };

  // Home banners
  getHomeBanners = async (): Promise<HomeBannerRow[]> => {
    const response = await adminAPI.get<
      HomeBannerRow[] | PaginatedResponse<HomeBannerRow>
    >("/home-banners/");
    const d = response.data;
    if (Array.isArray(d)) {
      return d;
    }
    return d.results ?? [];
  };

  createHomeBanner = async (
    data: Partial<HomeBannerRow>
  ): Promise<HomeBannerRow> => {
    const response = await adminAPI.post<HomeBannerRow>("/home-banners/", data);
    return response.data;
  };

  patchHomeBanner = async (
    id: number,
    data: Partial<HomeBannerRow>
  ): Promise<HomeBannerRow> => {
    const response = await adminAPI.patch<HomeBannerRow>(
      `/home-banners/${id}/`,
      data
    );
    return response.data;
  };

  deleteHomeBanner = async (id: number): Promise<void> => {
    await adminAPI.delete(`/home-banners/${id}/`);
  };

  // Committee / Contact — card sections (`?page_slug=committee|contact`)
  getSitePageSections = async (
    pageSlug: "committee" | "contact"
  ): Promise<SitePageCardSectionRow[]> => {
    const response = await adminAPI.get<
      SitePageCardSectionRow[] | PaginatedResponse<SitePageCardSectionRow>
    >("/site-page-sections/", { params: { page_slug: pageSlug } });
    const d = response.data;
    if (Array.isArray(d)) {
      return d;
    }
    return d.results ?? [];
  };

  createSitePageSection = async (data: {
    page_slug: "committee" | "contact";
    title: string;
    sort_order?: number;
    grid_columns?: number;
    is_active?: boolean;
  }): Promise<SitePageCardSectionRow> => {
    const response = await adminAPI.post<SitePageCardSectionRow>(
      "/site-page-sections/",
      data
    );
    return response.data;
  };

  patchSitePageSection = async (
    id: number,
    data: Partial<
      Pick<
        SitePageCardSectionRow,
        "title" | "sort_order" | "grid_columns" | "is_active"
      >
    >
  ): Promise<SitePageCardSectionRow> => {
    const response = await adminAPI.patch<SitePageCardSectionRow>(
      `/site-page-sections/${id}/`,
      data
    );
    return response.data;
  };

  deleteSitePageSection = async (id: number): Promise<void> => {
    await adminAPI.delete(`/site-page-sections/${id}/`);
  };

  createSitePageCard = async (data: {
    section: number;
    sort_order?: number;
    name: string;
    email?: string;
    phone?: string;
    role?: string;
    detail_line?: string;
    is_active?: boolean;
  }): Promise<SitePageCardRow> => {
    const response = await adminAPI.post<SitePageCardRow>(
      "/site-page-cards/",
      data
    );
    return response.data;
  };

  patchSitePageCard = async (
    id: number,
    data: Partial<
      Pick<
        SitePageCardRow,
        | "section"
        | "sort_order"
        | "name"
        | "email"
        | "phone"
        | "role"
        | "detail_line"
        | "is_active"
      >
    >
  ): Promise<SitePageCardRow> => {
    const response = await adminAPI.patch<SitePageCardRow>(
      `/site-page-cards/${id}/`,
      data
    );
    return response.data;
  };

  deleteSitePageCard = async (id: number): Promise<void> => {
    await adminAPI.delete(`/site-page-cards/${id}/`);
  };
}

export const adminService = new AdminService();
